import { Button, Dialog, Field, Icon, Input } from '@dayon/design-system';
import { formatCount } from '@dayon/money';

export interface ExportOutcome {
  ref: string;
  rowCount: number;
  truncated: boolean;
  rowCap: number;
  from: string;
  to: string;
  brandScope: string[] | null;
  downloadPath: string;
}

/**
 * Export the audit trail.
 *
 * The file is produced by the reports engine — the same closed-map builder,
 * row cap, statement timeout, read-only database role and CSV formula guard as
 * every other extract — rather than by a second CSV path written here. What
 * this dialog collects is the one thing the operator decides: the window. The
 * brand scope comes from their session and is never sent, so an export cannot
 * carry a brand its requester could not open.
 *
 * WHAT IS ACTUALLY EXTRACTED is `platform.audit_log`: the append-only,
 * hash-chained record of privileged acts, in which every row carries its own
 * fingerprint and the previous row's, which is the property that lets whoever
 * receives the file walk the chain and satisfy themselves that nothing was
 * removed. It is emphatically NOT `identity.auth_attempt`, the sign-in trail
 * drawn on the Audit log tab of Access control, and the first notice spends a
 * whole sentence on that distinction because the two logs sit one click apart
 * and an investigator handed the wrong file would be reading failed passwords
 * where they expected role changes.
 *
 * The notice names neither table, because neither name is of any use to the
 * person reading it: an operator cannot look `platform.audit_log` up, ask
 * anybody for it, or repeat it to a regulator. What they can use is the
 * DIFFERENCE between the two logs — what somebody did, versus who tried to get
 * in — so that is what the screen says and the identifiers stay here.
 *
 * The contrast is also worded without pointing at a tab. This dialog is opened
 * from two screens: the Audit log tab of Access control, where a sign-in trail
 * really is sitting behind it, and the system administration screen's "Export
 * logs" button, where there is no such tab at all — so the older wording, "the
 * sign-in trail on this tab", was untrue half the time it was read.
 *
 * WHO CAN DOWNLOAD IT AGAIN. The file is kept after it is made and listed in
 * Run history, and the platform serves it to admins whose access covers every
 * brand, to the people who can see the brand it is kept under, and to whoever
 * made it (ruling D5). An admin held to one brand has it kept under that brand;
 * one held to several, or to none, has it kept under no brand, so only they and
 * admins with every brand can. The dialog says which before Run.
 */
export function ExportDialog({
  open,
  onClose,
  from,
  to,
  onFrom,
  onTo,
  errors,
  busy,
  outcome,
  scopedBrandNames,
  onSubmit,
  onDownload,
}: {
  open: boolean;
  onClose: () => void;
  from: string;
  to: string;
  onFrom: (v: string) => void;
  onTo: (v: string) => void;
  errors: Record<string, string>;
  busy: boolean;
  outcome: ExportOutcome | null;
  /** Non-empty only when the signed-in admin is restricted to some brands. */
  scopedBrandNames: string[];
  onSubmit: () => void;
  onDownload: () => void;
}) {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Export audit log"
      wide
      dismissible={!busy}
      actions={
        <>
          <Button onClick={onClose} disabled={busy}>
            {outcome ? 'Close' : 'Cancel'}
          </Button>
          {outcome ? (
            <Button variant="primary" onClick={onDownload}>
              <Icon name="download-simple" />
              Download CSV
            </Button>
          ) : (
            <Button variant="primary" onClick={onSubmit} disabled={busy}>
              {busy ? 'Running…' : 'Run the export'}
            </Button>
          )}
        </>
      }
    >
      <div className="stack">
        <div className="notice notice-quiet">
          <Icon name="info" />
          <div>
            This extracts the <strong>privileged-action trail</strong> — the permanent record of
            what administrators did to the platform. It is not the sign-in trail, which records only
            who tried to sign in and when; the two are kept as separate logs. Every row carries its
            actor, action, subject, the before and after of what changed, and its
            <strong>fingerprint and the previous row&rsquo;s fingerprint</strong>, so whoever
            receives the file can walk the chain themselves and confirm nothing was removed or
            altered, rather than taking it on trust. Timestamps in the file are UTC and labelled
            UTC: a CSV lands somewhere with no timezone context, and an unlabelled local time in a
            regulatory file is how two parties end up disagreeing about the order of two events.
            The From and To days, though, are Manila gaming days, midnight to midnight Manila time,
            as every report counts them. So the file covers 16:00 UTC on the day before From to
            15:59:59 UTC on the To day.
          </div>
        </div>

        <div className="notice notice-quiet">
          <Icon name="warning-circle" />
          <div>
            <strong>Exporting the audit log is itself audited.</strong> Running this appends two
            rows to the trail you are exporting — the report run, and the export — naming you, the
            window and how many rows left the platform.
            {scopedBrandNames.length > 0 ? (
              <>
                {' '}
                Your access is restricted to {scopedBrandNames.join(', ')}, so the file carries only
                rows attributed to {scopedBrandNames.length === 1 ? 'that brand' : 'those brands'}.
                Most privileged acts are platform-wide and carry no brand at all — a role change, a
                suspension, a settings edit — and those are <strong>not</strong> in your file.{' '}
                {scopedBrandNames.length === 1 ? (
                  <>
                    The file is kept under that brand: only people who can see{' '}
                    {scopedBrandNames[0]} can download it again.
                  </>
                ) : (
                  <>
                    It is kept under no one brand, so only you, and admins whose access covers every
                    brand, can download it again.
                  </>
                )}
              </>
            ) : (
              ' Your access is not restricted to any brand, so the file carries every row in the window, brand-attributed or not. Only admins whose access covers every brand can download it again.'
            )}
          </div>
        </div>

        <div className="halves">
          <Field label="From" error={errors['from']}>
            {({ id, describedBy, invalid }) => (
              <Input
                id={id}
                type="date"
                value={from}
                onChange={(e) => onFrom(e.target.value)}
                aria-describedby={describedBy}
                aria-invalid={invalid || undefined}
              />
            )}
          </Field>
          <Field label="To" error={errors['to']}>
            {({ id, describedBy, invalid }) => (
              <Input
                id={id}
                type="date"
                value={to}
                onChange={(e) => onTo(e.target.value)}
                aria-describedby={describedBy}
                aria-invalid={invalid || undefined}
              />
            )}
          </Field>
        </div>

        {outcome && (
          <div className="notice notice-quiet">
            <Icon name={outcome.truncated ? 'warning-circle' : 'check-circle'} />
            <div>
              <strong>{outcome.ref}</strong> · {formatCount(outcome.rowCount)} row
              {outcome.rowCount === 1 ? '' : 's'} for {outcome.from} to {outcome.to}.
              {outcome.truncated ? (
                <>
                  {' '}
                  <strong>
                    That is the {formatCount(outcome.rowCap)}-row cap, so the file is INCOMPLETE.
                  </strong>{' '}
                  Narrow the window and run it again — a truncated audit extract presented as a
                  complete one is worse than no extract.
                </>
              ) : (
                ' The file is stored against that reference, so downloading it again a week from now returns the same bytes.'
              )}
            </div>
          </div>
        )}
      </div>
    </Dialog>
  );
}
